import { SearchResult, InventoryItem } from "@/types/inventory";

export interface StreamState {
  currentResult: SearchResult;
  itemsMap: Map<string, InventoryItem>;
  hasReceivedProducts: boolean;
  expectedItemCount: number;
  placeholderItemsCreated: boolean;
  receivedEvents: string[]; // Track events received for debugging
}

export const createInitialState = (searchTerm: string): StreamState => ({
  currentResult: {
    searchTerm,
    items: [],
    timestamp: new Date().toISOString()
  },
  itemsMap: new Map(),
  hasReceivedProducts: false,
  expectedItemCount: 0,
  placeholderItemsCreated: false,
  receivedEvents: []
});

export const handleStartEvent = (data: any, state: StreamState): void => {
  state.receivedEvents.push(`start-${Date.now()}`);
};

export const handleInitialSummaryEvent = (
  data: any,
  state: StreamState,
  onUpdate: (result: SearchResult) => void
): void => {
  state.receivedEvents.push(`initial-summary-${Date.now()}-items:${data.items?.length || 0}`);
  state.hasReceivedProducts = true;
  
  if (!data.items || data.items.length === 0) {
    return;
  }
  
  // Create items from initial summary with basic info
  data.items.forEach((item: any, i: number) => {
    const inventoryItem: InventoryItem = {
      id: item.sku || `${state.currentResult.searchTerm.toLowerCase().replace(/\s+/g, "-")}-${i}`,
      name: item.name,
      category: "Whiskey",
      price: item.currentPrice || item.price || "$0.00",
      size: "750 ML",
      storeQty: 0, // Initialize to 0, will be updated by product-update if store data comes
      warehouseQty: parseInt(item.warehouseQty) || 0,
      onOrderQty: parseInt(item.onOrderQty) || 0,
      location: "Various Utah Stores",
      lastChecked: new Date().toISOString(),
      stores: []
    };
    
    if (item.sku) {
      state.itemsMap.set(item.sku, inventoryItem);
    }
  });
  
  // Update the result with initial items
  state.currentResult = {
    searchTerm: state.currentResult.searchTerm,
    items: Array.from(state.itemsMap.values()),
    timestamp: new Date().toISOString()
  };
  
  onUpdate(state.currentResult);
};

export const handleProductUpdateEvent = (
  data: any,
  state: StreamState,
  onUpdate: (result: SearchResult) => void
): void => {
  state.receivedEvents.push(`product-update-${Date.now()}-sku:${data.sku}`);
  
  // Find and update the existing item
  if (!state.itemsMap.has(data.sku)) {
    return;
  }
  
  const item = state.itemsMap.get(data.sku)!;
  
  // Store the original values to preserve them completely
  const originalPrice = item.price;
  const originalWarehouseQty = item.warehouseQty;
  const originalOnOrderQty = item.onOrderQty;
  
  // Update with store data but preserve ALL original values from initial summary
  if (data.stores && Array.isArray(data.stores)) {
    item.stores = data.stores.map((store: any) => ({
      storeNumber: store.storeNumber,
      address: store.address || "",
      city: store.city,
      phone: store.phone || "",
      qty: parseInt(store.qty) || 0,
      name: store.storeNumber || ""
    }));
    
    // Calculate total store quantity
    item.storeQty = item.stores.reduce((total, store) => total + store.qty, 0);
  }
  
  // Update the result
  state.currentResult = {
    searchTerm: state.currentResult.searchTerm,
    items: Array.from(state.itemsMap.values()),
    timestamp: new Date().toISOString()
  };
  
  onUpdate(state.currentResult);
};

export const handleStatusEvent = (
  data: any,
  state: StreamState,
  onUpdate: (result: SearchResult) => void
): void => {
  state.receivedEvents.push(`status-${Date.now()}-msg:${data.message?.substring(0, 50) || 'no-msg'}`);
  
  // Look for messages that indicate total item count
  if (data.message && data.message.includes('Found ') && data.message.includes(' items')) {
    const match = data.message.match(/Found (\d+) items/);
    if (match) {
      state.expectedItemCount = parseInt(match[1], 10);
      
      // If we haven't received products/initial-summary and haven't created placeholders yet, create them
      if (!state.hasReceivedProducts && !state.placeholderItemsCreated && state.expectedItemCount > 0) {
        state.placeholderItemsCreated = true;
        
        // Create placeholder items
        for (let i = 0; i < state.expectedItemCount; i++) {
          const placeholderId = `placeholder-${state.currentResult.searchTerm.toLowerCase().replace(/\s+/g, "-")}-${i}`;
          const placeholderItem: InventoryItem = {
            id: placeholderId,
            name: "Loading...",
            category: "Whiskey",
            price: "$0.00",
            size: "750 ML",
            storeQty: 0,
            warehouseQty: 0,
            onOrderQty: 0,
            location: "Various Utah Stores",
            lastChecked: new Date().toISOString(),
            stores: []
          };
          
          state.itemsMap.set(placeholderId, placeholderItem);
        }
        
        // Update the result with placeholder items
        state.currentResult = {
          searchTerm: state.currentResult.searchTerm,
          items: Array.from(state.itemsMap.values()),
          timestamp: new Date().toISOString()
        };
        
        onUpdate(state.currentResult);
      }
    }
  }
};

export const handleItemStartEvent = (
  data: any,
  state: StreamState,
  onUpdate: (result: SearchResult) => void
): void => {
  state.receivedEvents.push(`item-start-${Date.now()}-item:${data.itemName?.substring(0, 20) || 'unknown'}`);
  
  // If we have placeholder items, update the first "Loading..." one
  const placeholderItems = Array.from(state.itemsMap.values()).filter(item => item.name === "Loading...");
  if (placeholderItems.length > 0) {
    const placeholder = placeholderItems[0];
    placeholder.name = data.itemName;
  } else if (!state.hasReceivedProducts) {
    // If no placeholders and no products event, create item from item-start
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    const basicItem: InventoryItem = {
      id: tempId,
      name: data.itemName,
      category: "Whiskey",
      price: "$0.00",
      size: "750 ML",
      storeQty: 0,
      warehouseQty: 0,
      onOrderQty: 0,
      location: "Various Utah Stores",
      lastChecked: new Date().toISOString(),
      stores: []
    };
    
    state.itemsMap.set(tempId, basicItem);
  }
  
  // Update the result
  state.currentResult = {
    searchTerm: state.currentResult.searchTerm,
    items: Array.from(state.itemsMap.values()),
    timestamp: new Date().toISOString()
  };
  
  onUpdate(state.currentResult);
};

export const handleItemUpdateEvent = (
  data: any,
  state: StreamState,
  onUpdate: (result: SearchResult) => void
): void => {
  state.receivedEvents.push(`item-update-${Date.now()}-sku:${data.sku}-name:${data.name?.substring(0, 20) || 'unknown'}`);
  
  // If we already have this item by SKU, update it
  if (state.itemsMap.has(data.sku)) {
    const item = state.itemsMap.get(data.sku)!;
    
    // Preserve the original values when updating with store data
    const originalPrice = item.price;
    const originalWarehouseQty = item.warehouseQty;
    const originalOnOrderQty = item.onOrderQty;
    
    item.name = data.name;
    
    // Update price if we have price data from the backend, or if original was placeholder
    const newPrice = data.currentPrice || data.price;
    if (newPrice && newPrice !== "$0.00") {
      item.price = newPrice;
    }
    
    // Only update warehouse and on-order quantities if they have meaningful values AND the original was 0
    if (data.warehouseQty !== undefined && data.warehouseQty !== null && originalWarehouseQty === 0) {
      item.warehouseQty = parseInt(data.warehouseQty) || 0;
    }
    
    if (data.onOrderQty !== undefined && data.onOrderQty !== null && originalOnOrderQty === 0) {
      item.onOrderQty = parseInt(data.onOrderQty) || 0;
    }
    
    // Update with store data if available
    if (data.stores && data.stores.length > 0) {
      item.stores = data.stores.map((store: any) => ({
        storeNumber: store.storeNumber,
        address: store.address || "",
        city: store.city,
        phone: store.phone || "",
        qty: parseInt(store.qty) || 0,
        name: store.storeNumber || ""
      }));
      
      item.storeQty = item.stores.reduce((total, store) => total + store.qty, 0);
    }
  } else {
    // Create new item from item-update event
    const inventoryItem: InventoryItem = {
      id: data.sku || `${state.currentResult.searchTerm.toLowerCase().replace(/\s+/g, "-")}-${state.itemsMap.size}`,
      name: data.name,
      category: "Whiskey",
      price: data.currentPrice || data.price || "$0.00",
      size: "750 ML",
      storeQty: 0,
      warehouseQty: parseInt(data.warehouseQty) || 0,
      onOrderQty: parseInt(data.onOrderQty) || 0,
      location: "Various Utah Stores",
      lastChecked: new Date().toISOString(),
      stores: data.stores ? data.stores.map((store: any) => ({
        storeNumber: store.storeNumber,
        address: store.address || "",
        city: store.city,
        phone: store.phone || "",
        qty: parseInt(store.qty) || 0,
        name: store.storeNumber || ""
      })) : []
    };
    
    // Calculate store quantity if stores data exists
    if (inventoryItem.stores.length > 0) {
      inventoryItem.storeQty = inventoryItem.stores.reduce((total, store) => total + store.qty, 0);
    }
    
    // If there's a temp item with the same name, remove it
    const tempItems = Array.from(state.itemsMap.entries()).filter(([key, item]) => 
      item.name === data.name && key.startsWith('temp-')
    );
    
    if (tempItems.length > 0) {
      const [tempKey] = tempItems[0];
      state.itemsMap.delete(tempKey);
    }
    
    // Also check for placeholder items and replace the first one
    const placeholderEntries = Array.from(state.itemsMap.entries()).filter(([key, item]) => 
      item.name === "Loading..." && key.startsWith('placeholder-')
    );
    
    if (placeholderEntries.length > 0) {
      const [placeholderKey] = placeholderEntries[0];
      state.itemsMap.delete(placeholderKey);
    }
    
    state.itemsMap.set(inventoryItem.id, inventoryItem);
  }
  
  // Update the result
  state.currentResult = {
    searchTerm: state.currentResult.searchTerm,
    items: Array.from(state.itemsMap.values()),
    timestamp: new Date().toISOString()
  };
  
  onUpdate(state.currentResult);
};

export const handleTooManyResultsEvent = (
  data: any,
  state: StreamState,
  onUpdate: (result: SearchResult) => void,
  onComplete: (finalResult: SearchResult) => void
): void => {
  state.receivedEvents.push(`too-many-results-${Date.now()}`);
  
  state.currentResult = {
    searchTerm: state.currentResult.searchTerm,
    items: [],
    timestamp: new Date().toISOString(),
    too_many_results: true,
    totalItemsFromAPI: data.totalItemsFromAPI || data.count || 0,
    maxAllowedItems: data.maxAllowedItems || data.threshold || 10
  };
  
  onUpdate(state.currentResult);
  onComplete(state.currentResult);
};

export const handleCompleteEvent = (
  data: any,
  state: StreamState,
  onUpdate: (result: SearchResult) => void,
  onComplete: (finalResult: SearchResult) => void
): void => {
  state.receivedEvents.push(`complete-${Date.now()}`);
  
  // Check if the completion indicates too many results
  if (data.success === false && data.reason === 'too_many_results') {
    state.currentResult = {
      searchTerm: state.currentResult.searchTerm,
      items: [],
      timestamp: new Date().toISOString(),
      too_many_results: true,
      totalItemsFromAPI: data.count || 0,
      maxAllowedItems: data.maxAllowedItems || data.threshold || 10
    };
    
    onUpdate(state.currentResult);
    onComplete(state.currentResult);
    return;
  }
  
  // Remove any remaining placeholder items
  const finalItems = Array.from(state.itemsMap.values()).filter(item => item.name !== "Loading...");
  
  // Update the current result with final items but preserve all their current state
  state.currentResult = {
    searchTerm: state.currentResult.searchTerm,
    items: finalItems,
    timestamp: new Date().toISOString()
  };
  
  onComplete(state.currentResult);
};
